// Exception Register — PIV exemptions and policy exceptions
// Migrated to Cyan Room design system

import { pbAuthedFetch } from '../authHeaders';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTableControls, type Column } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';

export function ExceptionsPage() {
  const [showVIPDetails, setShowVIPDetails] = useState(false);
  const [expandedOffender, setExpandedOffender] = useState<string | null>(null);

  const { data: exceptionsData, isLoading: excLoading, error: excError, refetch: refetchExceptions } = useQuery({
    queryKey: ['exceptions'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/exceptions');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: repeatData } = useQuery({
    queryKey: ['exceptions-repeat-offenders'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/exceptions/repeat-offenders');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: oconusData } = useQuery({
    queryKey: ['exceptions-oconus'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/exceptions/oconus');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: vipData } = useQuery({
    queryKey: ['exceptions-vip'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/exceptions/vip');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: expiryData } = useQuery({
    queryKey: ['exceptions-expiry-runway'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/exceptions/expiry-runway');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const exceptions = exceptionsData?.exceptions || [];
  const summary = exceptionsData?.summary || {};
  const repeatOffenders = repeatData?.offenders || [];
  const oconusLocations = oconusData?.locations || [];
  const vipSummary = vipData?.summary || {};
  const vipDetails = vipData?.details || [];
  const expiryRunway = expiryData?.runway || {};
  const expiredNotRevoked = expiryData?.expired_not_revoked || [];

  // Column definitions and useTableControls() calls must run on every render
  // (including the loading/error early-returns below) — they're hooks, and
  // conditionally reaching them only once data has loaded violates the Rules
  // of Hooks (React error #310, "rendered more hooks than during the
  // previous render"). The empty-array fallbacks above make this safe: these
  // just operate on [] until the real data arrives.

  // Main register table columns
  const registerColumns: Column<any>[] = [
    {
      key: 'subject_account',
      label: 'Subject Account',
      sortable: true,
      accessor: (r: any) => r.subject_account,
      render: (r: any) => <span className="td--id">{r.subject_account}</span>,
    },
    {
      key: 'exception_type',
      label: 'Type',
      sortable: true,
      accessor: (r: any) => r.exception_type,
      render: (r: any) => <span className="pill"><i></i>{r.exception_type}</span>,
      filterOptions: [
        { value: 'piv_exemption', label: 'PIV' },
        { value: 'access_override', label: 'Access' },
        { value: 'policy_exception', label: 'Policy' },
        { value: 'compliance_waiver', label: 'Compliance' },
      ],
    },
    {
      key: 'reason',
      label: 'Reason',
      sortable: true,
      accessor: (r: any) => r.reason,
      render: (r: any) => <span style={{ fontSize: 'var(--text-sm)' }}>{r.reason || '—'}</span>,
    },
    {
      key: 'granted_at',
      label: 'Granted',
      sortable: true,
      accessor: (r: any) => r.granted_at,
      render: (r: any) => (
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
          {r.granted_at ? new Date(r.granted_at).toLocaleDateString() : '—'}
        </span>
      ),
      dateField: true,
    },
    {
      key: 'expires_at',
      label: 'Expires',
      sortable: true,
      accessor: (r: any) => r.expires_at,
      render: (r: any) => {
        if (!r.expires_at) return <span style={{ color: 'var(--ink-45)' }}>—</span>;
        const exp = new Date(r.expires_at);
        const daysLeft = Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        const isExpired = daysLeft < 0;
        const isSoon = daysLeft >= 0 && daysLeft <= 30;
        return (
          <span className={`pill ${isExpired ? 'pill--risk' : isSoon ? 'pill--warn' : ''}`} style={(!isExpired && !isSoon) ? { fontSize: 'var(--text-sm)', color: 'var(--ink-45)' } : undefined}>
            {isExpired && <i></i>}
            {isSoon && <i></i>}
            {exp.toLocaleDateString()}
            {isExpired && ' (expired)'}
            {isSoon && ` (${daysLeft}d)`}
          </span>
        );
      },
      dateField: true,
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      accessor: (r: any) => r.location,
      render: (r: any) => (
        <span>
          {r.location || '—'}
          {r.is_oconus && <span className="pill pill--warn" style={{ marginLeft: 'var(--sp-2)' }}><i></i>OCONUS</span>}
        </span>
      ),
    },
    {
      key: 'is_vip',
      label: 'VIP',
      sortable: true,
      accessor: (r: any) => r.is_vip,
      render: (r: any) => r.is_vip ? <span className="pill pill--warn"><i></i>VIP</span> : <span style={{ color: 'var(--ink-45)' }}>—</span>,
    },
    {
      key: 'recurrence_count',
      label: 'Recurrence',
      sortable: true,
      accessor: (r: any) => r.recurrence_count,
      render: (r: any) => (
        <span
          className="td--num"
          style={{
            fontWeight: r.recurrence_count > 1 ? 600 : 400,
            color: r.recurrence_count > 1 ? 'var(--risk)' : undefined,
          }}
        >
          {r.recurrence_count ?? 1}
        </span>
      ),
    },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      accessor: (r: any) => r.state,
      render: (r: any) => {
        const state = r.state || 'active';
        const pillClass = state === 'active' ? 'pill--ok' : state === 'expired' ? 'pill--risk' : state === 'revoked' ? '' : 'pill--warn';
        return <span className={`pill ${pillClass}`}><i></i>{state}</span>;
      },
      filterOptions: [
        { value: 'active', label: 'Active' },
        { value: 'expired', label: 'Expired' },
        { value: 'revoked', label: 'Revoked' },
        { value: 'pending_renewal', label: 'Pending Renewal' },
      ],
    },
  ];

  const registerCtrl = useTableControls(exceptions, registerColumns, {
    storageKey: 'exceptions-register',
    keyAccessor: (r: any) => r.id,
  });

  // Repeat offenders columns
  const offenderColumns: Column<any>[] = [
    {
      key: 'subject_account',
      label: 'Subject Account',
      sortable: true,
      accessor: (r: any) => r.subject_account,
      render: (r: any) => <span className="td--id">{r.subject_account}</span>,
    },
    {
      key: 'exception_count',
      label: 'Exception Count',
      sortable: true,
      accessor: (r: any) => r.exception_count,
      render: (r: any) => (
        <span className="td--num" style={{ fontWeight: 600, color: 'var(--risk)' }}>
          {r.exception_count}
        </span>
      ),
    },
    {
      key: 'first_granted',
      label: 'First Exception',
      sortable: true,
      accessor: (r: any) => r.first_granted,
      render: (r: any) => (
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
          {r.first_granted ? new Date(r.first_granted).toLocaleDateString() : '—'}
        </span>
      ),
      dateField: true,
    },
    {
      key: 'latest_granted',
      label: 'Latest Exception',
      sortable: true,
      accessor: (r: any) => r.latest_granted,
      render: (r: any) => (
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
          {r.latest_granted ? new Date(r.latest_granted).toLocaleDateString() : '—'}
        </span>
      ),
      dateField: true,
    },
  ];
  const offenderCtrl = useTableControls(repeatOffenders, offenderColumns, {
    storageKey: 'exceptions-repeat',
    defaultSortKey: 'exception_count',
    defaultSortDir: 'desc',
    keyAccessor: (r: any) => r.subject_account,
  });

  // OCONUS columns
  const oconusColumns: Column<any>[] = [
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      accessor: (r: any) => r.location,
      render: (r: any) => <span className="td--name">{r.location}</span>,
    },
    {
      key: 'exception_count',
      label: 'Exceptions',
      sortable: true,
      accessor: (r: any) => r.exception_count,
      render: (r: any) => <span className="td--num" style={{ fontWeight: 600 }}>{r.exception_count}</span>,
    },
    {
      key: 'avg_duration_days',
      label: 'Avg Duration',
      sortable: true,
      accessor: (r: any) => r.avg_duration_days,
      render: (r: any) => <span className="td--num">{r.avg_duration_days != null ? `${r.avg_duration_days}d` : '—'}</span>,
    },
    {
      key: 'max_duration_days',
      label: 'Max Duration',
      sortable: true,
      accessor: (r: any) => r.max_duration_days,
      render: (r: any) => <span className="td--num">{r.max_duration_days != null ? `${r.max_duration_days}d` : '—'}</span>,
    },
  ];
  const oconusCtrl = useTableControls(oconusLocations, oconusColumns, {
    storageKey: 'exceptions-oconus',
    keyAccessor: (r: any) => r.location,
  });

  // Expired-not-revoked columns
  const expiredColumns: Column<any>[] = [
    {
      key: 'subject_account',
      label: 'Subject Account',
      sortable: true,
      accessor: (r: any) => r.subject_account,
      render: (r: any) => <span className="td--id">{r.subject_account}</span>,
    },
    {
      key: 'exception_type',
      label: 'Type',
      sortable: true,
      accessor: (r: any) => r.exception_type,
      render: (r: any) => <span className="pill"><i></i>{r.exception_type}</span>,
    },
    {
      key: 'expired_at',
      label: 'Expired',
      sortable: true,
      accessor: (r: any) => r.expired_at,
      render: (r: any) => {
        if (!r.expired_at) return <span style={{ color: 'var(--ink-45)' }}>—</span>;
        const daysAgo = Math.floor((Date.now() - new Date(r.expired_at).getTime()) / (1000 * 60 * 60 * 24));
        return (
          <span className="pill pill--risk">
            <i></i>
            {new Date(r.expired_at).toLocaleDateString()} ({daysAgo}d ago)
          </span>
        );
      },
      dateField: true,
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      accessor: (r: any) => r.location,
      render: (r: any) => <span style={{ fontSize: 'var(--text-sm)' }}>{r.location || '—'}</span>,
    },
  ];
  const expiredCtrl = useTableControls(expiredNotRevoked, expiredColumns, {
    storageKey: 'exceptions-expired',
    keyAccessor: (r: any) => r.id,
  });

  // Loading state
  if (excLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Compliance</p>
              <h1 className="pagehead__title">Exception Register</h1>
              <div className="pagehead__facts">
                <span className="fact">Policy exceptions &amp; PIV exemptions</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading exception data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (excError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Compliance</p>
              <h1 className="pagehead__title">Exception Register</h1>
              <div className="pagehead__facts">
                <span className="fact">Policy exceptions &amp; PIV exemptions</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load exception data</h3>
                <p className="state__body">
                  Could not connect to the exceptions API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetchExceptions()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Compliance</p>
            <h1 className="pagehead__title">Exception Register</h1>
            <div className="pagehead__facts">
              <span className="fact">Policy exceptions &amp; PIV exemptions</span>
              <span className="fact">{exceptions.length} exceptions tracked</span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Summary indicators */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Exception summary">
          <div className="ind">
            <div className="ind__head">
              <p className="ind__abbr">Active</p>
              <p className="ind__full">Currently granted exceptions</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary.active_count ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--ok)' }}></i>
              <b>Granted</b> and not expired
            </p>
          </div>

          <div className="ind ind--breach">
            <div className="ind__head">
              <p className="ind__abbr">Repeat</p>
              <p className="ind__full">Subjects with &gt;1 exception</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary.repeat_offender_count ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i></i>
              <b>Action required</b> — recurring exceptions detected
            </p>
          </div>

          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">OCONUS</p>
              <p className="ind__full">Overseas location exceptions</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary.oconus_count ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--gold)' }}>Monitor</b> — overseas locations
            </p>
          </div>

          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">VIP</p>
              <p className="ind__full">Flagged VIP exceptions</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary.vip_count ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--gold)' }}>Sensitive</b> — politically flagged
            </p>
          </div>

          <div className="ind ind--breach">
            <div className="ind__head">
              <p className="ind__abbr">Expired</p>
              <p className="ind__full">Past expiry, not revoked</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{summary.expired_not_revoked_count ?? '—'}</p>
            </div>
            <p className="ind__reading">
              <i></i>
              <b>Critical</b> — access still active past expiry
            </p>
          </div>
        </div>

        {/* Register table */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Register</p>
              <h2 className="box__title">Exception Register</h2>
            </div>
            <div className="box__head-actions">
              <span className="fact">{exceptions.length} exceptions</span>
            </div>
          </div>
          <div className="box__body--flush">
            {exceptions.length > 0 ? (
              <DataTable
                columns={registerColumns}
                visibleColumns={registerCtrl.visibleColumns}
                data={registerCtrl.paged}
                search={registerCtrl.search}
                onSearchChange={registerCtrl.setSearch}
                sortKey={registerCtrl.sortKey}
                sortDir={registerCtrl.sortDir}
                onSort={registerCtrl.setSort}
                page={registerCtrl.page}
                totalPages={registerCtrl.totalPages}
                totalFiltered={registerCtrl.totalFiltered}
                pageSize={registerCtrl.pageSize}
                onPageChange={registerCtrl.setPage}
                onPageSizeChange={registerCtrl.setPageSize}
                activeFilters={registerCtrl.activeFilters}
                onFilter={registerCtrl.setFilter}
                onClearFilters={registerCtrl.clearFilters}
                timeRange={registerCtrl.timeRange}
                onTimeRangeChange={registerCtrl.setTimeRange}
                hiddenColumns={registerCtrl.hiddenColumns}
                onToggleColumn={registerCtrl.toggleColumn}
                selectedIds={registerCtrl.selectedIds}
                onToggleSelect={registerCtrl.toggleSelect}
                onSelectAll={registerCtrl.selectAll}
                onClearSelection={registerCtrl.clearSelection}
                savedViews={registerCtrl.savedViews}
                onSaveView={registerCtrl.saveView}
                onLoadView={registerCtrl.loadView}
                onDeleteView={registerCtrl.deleteView}
                onExportCSV={registerCtrl.exportCSV}
                onExportExcel={registerCtrl.exportExcel}
                searchPlaceholder="Search exceptions..."
                keyAccessor={(r: any) => r.id}
                emptyMessage="No exceptions registered."
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <h3 className="state__title">No exceptions registered</h3>
                <p className="state__body">
                  When policy exceptions are granted, they will appear here for tracking and compliance.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Repeat offenders */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Patterns</p>
              <h2 className="box__title">Repeat Offenders</h2>
            </div>
            <div className="box__head-actions">
              <span className="fact">{repeatOffenders.length} subjects with &gt;1 exception</span>
            </div>
          </div>
          <div className="box__body--flush">
            {repeatOffenders.length > 0 ? (
              <DataTable
                columns={offenderColumns}
                visibleColumns={offenderCtrl.visibleColumns}
                data={offenderCtrl.paged}
                search={offenderCtrl.search}
                onSearchChange={offenderCtrl.setSearch}
                sortKey={offenderCtrl.sortKey}
                sortDir={offenderCtrl.sortDir}
                onSort={offenderCtrl.setSort}
                page={offenderCtrl.page}
                totalPages={offenderCtrl.totalPages}
                totalFiltered={offenderCtrl.totalFiltered}
                pageSize={offenderCtrl.pageSize}
                onPageChange={offenderCtrl.setPage}
                onPageSizeChange={offenderCtrl.setPageSize}
                activeFilters={offenderCtrl.activeFilters}
                onFilter={offenderCtrl.setFilter}
                onClearFilters={offenderCtrl.clearFilters}
                timeRange={offenderCtrl.timeRange}
                onTimeRangeChange={offenderCtrl.setTimeRange}
                hiddenColumns={offenderCtrl.hiddenColumns}
                onToggleColumn={offenderCtrl.toggleColumn}
                selectedIds={offenderCtrl.selectedIds}
                onToggleSelect={offenderCtrl.toggleSelect}
                onSelectAll={offenderCtrl.selectAll}
                onClearSelection={offenderCtrl.clearSelection}
                savedViews={offenderCtrl.savedViews}
                onSaveView={offenderCtrl.saveView}
                onLoadView={offenderCtrl.loadView}
                onDeleteView={offenderCtrl.deleteView}
                onExportCSV={offenderCtrl.exportCSV}
                onExportExcel={offenderCtrl.exportExcel}
                searchPlaceholder="Search repeat offenders..."
                keyAccessor={(r: any) => r.subject_account}
                emptyMessage="No repeat offenders. Each subject has at most one exception."
                onRowClick={(r) => setExpandedOffender(expandedOffender === r.subject_account ? null : r.subject_account)}
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3 className="state__title">No repeat offenders</h3>
                <p className="state__body">
                  Each subject has at most one exception — no recurring patterns detected.
                </p>
              </div>
            )}
          </div>

          {/* Expanded history for selected offender */}
          {expandedOffender && (
            <div className="box__body" style={{ borderTop: '1px solid var(--hair)' }}>
              <h3 style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontWeight: 600, fontSize: 'var(--text-lg)', marginBottom: 'var(--sp-3)' }}>
                Exception History — {expandedOffender}
              </h3>
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th><button className="th th--static">Type</button></th>
                      <th><button className="th th--static">Reason</button></th>
                      <th><button className="th th--static">Granted</button></th>
                      <th><button className="th th--static">Expires</button></th>
                      <th><button className="th th--static">Location</button></th>
                      <th><button className="th th--static">State</button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {exceptions
                      .filter((e: any) => e.subject_account === expandedOffender)
                      .map((e: any) => (
                        <tr key={e.id}>
                          <td><span className="pill"><i></i>{e.exception_type}</span></td>
                          <td style={{ fontSize: 'var(--text-sm)' }}>{e.reason || '—'}</td>
                          <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                            {e.granted_at ? new Date(e.granted_at).toLocaleDateString() : '—'}
                          </td>
                          <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                            {e.expires_at ? new Date(e.expires_at).toLocaleDateString() : '—'}
                          </td>
                          <td style={{ fontSize: 'var(--text-sm)' }}>{e.location || '—'}</td>
                          <td>
                            <span className={`pill ${e.state === 'active' ? 'pill--ok' : e.state === 'expired' ? 'pill--risk' : ''}`}>
                              <i></i>{e.state || 'active'}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 'var(--sp-3)' }}>
                <button className="btn btn--sm" onClick={() => setExpandedOffender(null)}>
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* OCONUS breakdown */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Locations</p>
              <h2 className="box__title">OCONUS Breakdown</h2>
            </div>
            <div className="box__head-actions">
              <span className="fact">{oconusLocations.length} locations</span>
            </div>
          </div>
          <div className="box__body--flush">
            {oconusLocations.length > 0 ? (
              <DataTable
                columns={oconusColumns}
                visibleColumns={oconusCtrl.visibleColumns}
                data={oconusCtrl.paged}
                search={oconusCtrl.search}
                onSearchChange={oconusCtrl.setSearch}
                sortKey={oconusCtrl.sortKey}
                sortDir={oconusCtrl.sortDir}
                onSort={oconusCtrl.setSort}
                page={oconusCtrl.page}
                totalPages={oconusCtrl.totalPages}
                totalFiltered={oconusCtrl.totalFiltered}
                pageSize={oconusCtrl.pageSize}
                onPageChange={oconusCtrl.setPage}
                onPageSizeChange={oconusCtrl.setPageSize}
                activeFilters={oconusCtrl.activeFilters}
                onFilter={oconusCtrl.setFilter}
                onClearFilters={oconusCtrl.clearFilters}
                timeRange={oconusCtrl.timeRange}
                onTimeRangeChange={oconusCtrl.setTimeRange}
                hiddenColumns={oconusCtrl.hiddenColumns}
                onToggleColumn={oconusCtrl.toggleColumn}
                selectedIds={oconusCtrl.selectedIds}
                onToggleSelect={oconusCtrl.toggleSelect}
                onSelectAll={oconusCtrl.selectAll}
                onClearSelection={oconusCtrl.clearSelection}
                savedViews={oconusCtrl.savedViews}
                onSaveView={oconusCtrl.saveView}
                onLoadView={oconusCtrl.loadView}
                onDeleteView={oconusCtrl.deleteView}
                onExportCSV={oconusCtrl.exportCSV}
                onExportExcel={oconusCtrl.exportExcel}
                searchPlaceholder="Search locations..."
                keyAccessor={(r: any) => r.location}
                emptyMessage="No OCONUS exceptions."
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
                <h3 className="state__title">No OCONUS exceptions</h3>
                <p className="state__body">
                  No exceptions at overseas locations have been recorded.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* VIP breakdown */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Sensitive</p>
              <h2 className="box__title">VIP Exceptions</h2>
            </div>
          </div>
          <div className="box__body">
            <div className="notice notice--warn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div>
                <p className="notice__title">Sensitive Data</p>
                <p className="notice__body">
                  VIP data is politically sensitive — aggregate view shown by default.
                </p>
              </div>
            </div>

            {/* Aggregate view */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-4)', marginBottom: 'var(--sp-4)' }} role="group" aria-label="VIP summary">
              <div className="ind">
                <div className="ind__head">
                  <p className="ind__abbr">Total</p>
                  <p className="ind__full">VIP Exceptions</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{vipSummary.total ?? '—'}</p>
                </div>
              </div>
              <div className="ind">
                <div className="ind__head">
                  <p className="ind__abbr">Active</p>
                  <p className="ind__full">Active VIP</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{vipSummary.active ?? '—'}</p>
                </div>
              </div>
              <div className="ind">
                <div className="ind__head">
                  <p className="ind__abbr">Avg Days</p>
                  <p className="ind__full">Avg Duration (days)</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{vipSummary.avg_duration_days ?? '—'}</p>
                </div>
              </div>
            </div>

            {/* Toggle for detail */}
            <div style={{ marginBottom: 'var(--sp-4)' }}>
              <label className="cbx" style={{ fontSize: 'var(--text-sm)' }}>
                <input
                  type="checkbox"
                  checked={showVIPDetails}
                  onChange={e => setShowVIPDetails(e.target.checked)}
                />
                Show VIP details
              </label>
            </div>

            {showVIPDetails && vipDetails.length > 0 && (
              <div className="tbl-scroll">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th><button className="th th--static">Subject Account</button></th>
                      <th><button className="th th--static">Type</button></th>
                      <th><button className="th th--static">Reason</button></th>
                      <th><button className="th th--static">Granted</button></th>
                      <th><button className="th th--static">Expires</button></th>
                      <th><button className="th th--static">Location</button></th>
                    </tr>
                  </thead>
                  <tbody>
                    {vipDetails.map((v: any) => (
                      <tr key={v.id}>
                        <td className="td--id">{v.subject_account}</td>
                        <td><span className="pill"><i></i>{v.exception_type}</span></td>
                        <td style={{ fontSize: 'var(--text-sm)' }}>{v.reason || '—'}</td>
                        <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                          {v.granted_at ? new Date(v.granted_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-45)' }}>
                          {v.expires_at ? new Date(v.expires_at).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ fontSize: 'var(--text-sm)' }}>{v.location || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Expiry management */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Lifecycle</p>
              <h2 className="box__title">Expiry Management</h2>
            </div>
          </div>
          <div className="box__body">
            {/* Renewal runway */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--sp-4)', marginBottom: 'var(--sp-4)' }} role="group" aria-label="Expiry runway">
              <div className="ind ind--breach">
                <div className="ind__head">
                  <p className="ind__abbr">30d</p>
                  <p className="ind__full">Expiring in 30 days</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{expiryRunway.within_30_days ?? '—'}</p>
                </div>
                <p className="ind__reading">
                  <i></i>
                  <b>Urgent</b> — renewal action needed soon
                </p>
              </div>
              <div className="ind ind--watch">
                <div className="ind__head">
                  <p className="ind__abbr">60d</p>
                  <p className="ind__full">Expiring in 60 days</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{expiryRunway.within_60_days ?? '—'}</p>
                </div>
                <p className="ind__reading">
                  <i style={{ background: 'var(--gold)' }}></i>
                  <b style={{ color: 'var(--gold)' }}>Watch</b> — approaching renewal window
                </p>
              </div>
              <div className="ind">
                <div className="ind__head">
                  <p className="ind__abbr">90d</p>
                  <p className="ind__full">Expiring in 90 days</p>
                </div>
                <div className="ind__value">
                  <p className="ind__v">{expiryRunway.within_90_days ?? '—'}</p>
                </div>
                <p className="ind__reading">
                  <i style={{ background: 'var(--ok)' }}></i>
                  <b>Planned</b> — within planning horizon
                </p>
              </div>
            </div>

            {/* Expired but not revoked — critical findings */}
            {expiredNotRevoked.length > 0 && (
              <>
                <div className="notice notice--risk" style={{ marginBottom: 'var(--sp-3)' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <div>
                    <p className="notice__title">Critical Finding</p>
                    <p className="notice__body">
                      <strong>{expiredNotRevoked.length}</strong> exception{expiredNotRevoked.length !== 1 ? 's' : ''} expired but access has <strong>not been revoked</strong>. These are critical findings.
                    </p>
                  </div>
                </div>
                <div className="tbl-scroll">
                  <DataTable
                    columns={expiredColumns}
                    visibleColumns={expiredCtrl.visibleColumns}
                    data={expiredCtrl.paged}
                    search={expiredCtrl.search}
                    onSearchChange={expiredCtrl.setSearch}
                    sortKey={expiredCtrl.sortKey}
                    sortDir={expiredCtrl.sortDir}
                    onSort={expiredCtrl.setSort}
                    page={expiredCtrl.page}
                    totalPages={expiredCtrl.totalPages}
                    totalFiltered={expiredCtrl.totalFiltered}
                    pageSize={expiredCtrl.pageSize}
                    onPageChange={expiredCtrl.setPage}
                    onPageSizeChange={expiredCtrl.setPageSize}
                    activeFilters={expiredCtrl.activeFilters}
                    onFilter={expiredCtrl.setFilter}
                    onClearFilters={expiredCtrl.clearFilters}
                    timeRange={expiredCtrl.timeRange}
                    onTimeRangeChange={expiredCtrl.setTimeRange}
                    hiddenColumns={expiredCtrl.hiddenColumns}
                    onToggleColumn={expiredCtrl.toggleColumn}
                    selectedIds={expiredCtrl.selectedIds}
                    onToggleSelect={expiredCtrl.toggleSelect}
                    onSelectAll={expiredCtrl.selectAll}
                    onClearSelection={expiredCtrl.clearSelection}
                    savedViews={expiredCtrl.savedViews}
                    onSaveView={expiredCtrl.saveView}
                    onLoadView={expiredCtrl.loadView}
                    onDeleteView={expiredCtrl.deleteView}
                    onExportCSV={expiredCtrl.exportCSV}
                    onExportExcel={expiredCtrl.exportExcel}
                    searchPlaceholder="Search expired exceptions..."
                    keyAccessor={(r: any) => r.id}
                    emptyMessage="No expired exceptions found."
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
